import { createBrowserClient } from "@supabase/ssr";
import type { Provider, User, UserIdentity, SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "@/config";

// Create a Supabase client for client-side usage
export function createClient(): SupabaseClient<Database> {
  return createBrowserClient<Database>(
    SUPABASE_URL!,
    SUPABASE_ANON_KEY!
  );
}

// Get the current user on the client
export async function getUser() {
  const supabase = createClient();
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) return null;
  return user;
}

// Get the current session on the client
export async function getSession() {
  const supabase = createClient();
  const { data, error } = await supabase.auth.getSession();
  return { data, error };
}

// Sign in with a given OAuth provider
export async function signInWithProvider(provider: Provider, options?: { redirectTo?: string }) {
  const supabase = createClient();
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: options?.redirectTo || `${window.location.origin}/auth/callback`
    }
  });
  if (error) throw error;
  return data;
}

// Sign in with Google
export async function signInWithGoogle(options?: { redirectTo?: string }) {
  return signInWithProvider("google", options);
}

// Sign out the current user
export async function signOut() {
  const supabase = createClient();
  const { error } = await supabase.auth.signOut();
  return { error };
}

// Get linked identities for a user
export function getLinkedIdentities(user: User): UserIdentity[] {
  return user.identities || [];
}

// Get the primary identity for a user
export function getPrimaryIdentity(user: User): UserIdentity | null {
  if (!user.identities || user.identities.length === 0) {
    return null;
  }
  // The primary identity is the one created first.
  return user.identities.reduce((oldest, identity) => {
    return new Date(identity.created_at) < new Date(oldest.created_at) ? identity : oldest;
  });
} 